import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import PageTransition from './PageTransition';
import ThemeToggle from './ThemeToggle';
import './AppShell.css';

function initials(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase()).join('');
}

// Below this width the fixed sidebar would eat a third of the viewport, so
// it switches from an inline column to an off-canvas drawer. Kept in sync
// with the matching @media block in AppShell.css.
const MOBILE_QUERY = '(max-width: 900px)';

function useMediaQuery(query) {
  const [matches, setMatches] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(query).matches
  );

  useEffect(() => {
    const mql = window.matchMedia(query);
    const onChange = (e) => setMatches(e.matches);
    setMatches(mql.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [query]);

  return matches;
}

const MenuIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M3.5 6.5h17M3.5 12h17M3.5 17.5h17" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const SearchIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="11" cy="11" r="6.5" stroke="currentColor" strokeWidth="1.8" />
    <path d="M20 20l-3.8-3.8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const ClockIcon = () => (
  <svg width="19" height="19" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="12" r="8.5" stroke="currentColor" strokeWidth="1.7" />
    <path d="M12 7.5V12l3 1.8" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const BellIcon = () => (
  <svg width="19" height="19" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M6.5 9.5a5.5 5.5 0 0 1 11 0c0 4.5 1.5 5.8 1.5 5.8H5S6.5 14 6.5 9.5Z"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinejoin="round"
    />
    <path d="M10 18.5a2 2 0 0 0 4 0" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
  </svg>
);

const HelpIcon = () => (
  <svg width="19" height="19" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="12" r="8.5" stroke="currentColor" strokeWidth="1.7" />
    <path d="M9.6 9.4a2.4 2.4 0 0 1 4.7.6c0 1.6-2.3 2-2.3 3.6" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    <path d="M12 16.8v.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const SparkleIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M12 3.5l1.8 4.7L18.5 10l-4.7 1.8L12 16.5l-1.8-4.7L5.5 10l4.7-1.8Z"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinejoin="round"
    />
  </svg>
);

// Global chrome bar above the sidebar+content row, used only by the
// org-admin shell (variant="orgadmin") to match the reference product
// design — hamburger + brand on the left, client search center-left, and
// a right cluster of company name, What's new, clock, notifications, help,
// and an avatar menu (which holds Logout, since the sidebar no longer has
// a logout button).
export function TopBar({
  brandIcon,
  brandText,
  collapsed,
  onToggleCollapse,
  user,
  companyName,
  onLogout,
  notificationCount = 0,
  // A render prop, not a node: the panel is expensive enough (a list, its own
  // fetch on open) that mounting it behind every closed bell in the app would
  // be waste. Called only while open, and handed `close` so a row can dismiss
  // the panel as it navigates.
  renderNotifications = null,
  onSearch,
  searchPlaceholder,
}) {
  const [query, setQuery] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const [bellOpen, setBellOpen] = useState(false);
  const bellRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDocClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [menuOpen]);

  // Same outside-click dismissal as the avatar menu, plus Escape — the panel is
  // a list the user may be reading, and reaching for Escape is the reflex.
  useEffect(() => {
    if (!bellOpen) return undefined;
    const onDocClick = (e) => {
      if (bellRef.current && !bellRef.current.contains(e.target)) setBellOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setBellOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [bellOpen]);

  const handleSubmit = (event) => {
    event.preventDefault();
    if (onSearch) onSearch(query.trim());
  };

  return (
    <header className="ds-topbar">
      <div className="ds-topbar-left">
        <button
          type="button"
          className="ds-topbar-menu-btn"
          onClick={onToggleCollapse}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          <MenuIcon />
        </button>
        <div className="ds-topbar-brand">
          {brandIcon && <span className="ds-topbar-brand-icon">{brandIcon}</span>}
          <span className="ds-topbar-brand-text">{brandText}</span>
        </div>
      </div>

      {onSearch && (
        <form className="ds-topbar-search" onSubmit={handleSubmit}>
          <SearchIcon />
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={searchPlaceholder || 'Search...'}
          />
        </form>
      )}

      <div className="ds-topbar-right">
        {companyName && <span className="ds-topbar-company">{companyName}</span>}
        <button type="button" className="ds-topbar-whatsnew">
          <SparkleIcon />
          <span>What's new</span>
        </button>
        <button type="button" className="ds-topbar-icon-btn" title="Activity" aria-label="Activity">
          <ClockIcon />
        </button>
        <div className="ds-topbar-bell-wrap" ref={bellRef}>
          <button
            type="button"
            className="ds-topbar-icon-btn"
            title="Notifications"
            aria-label={notificationCount > 0 ? `Notifications (${notificationCount} unread)` : 'Notifications'}
            aria-haspopup={renderNotifications ? 'dialog' : undefined}
            aria-expanded={renderNotifications ? bellOpen : undefined}
            onClick={renderNotifications ? () => setBellOpen((o) => !o) : undefined}
          >
            <BellIcon />
            {notificationCount > 0 && (
              <span className="ds-topbar-badge">{notificationCount > 99 ? '99+' : notificationCount}</span>
            )}
          </button>
          {bellOpen && renderNotifications && renderNotifications({ close: () => setBellOpen(false) })}
        </div>
        <button type="button" className="ds-topbar-icon-btn" title="Help" aria-label="Help">
          <HelpIcon />
        </button>
        <div className="ds-topbar-avatar-wrap" ref={menuRef}>
          <button
            type="button"
            className="ds-topbar-avatar"
            onClick={() => setMenuOpen((o) => !o)}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            title={user?.name || 'Account'}
          >
            {initials(user?.name)}
          </button>
          <AnimatePresence>
            {menuOpen && (
              <motion.div
                className="ds-topbar-menu"
                role="menu"
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.15, ease: [0.4, 0, 0.2, 1] }}
              >
                <div className="ds-topbar-menu-head">
                  <div className="ds-topbar-menu-name">{user?.name}</div>
                  {user?.role && <div className="ds-topbar-menu-role">{user.role}</div>}
                </div>
                <button type="button" className="ds-topbar-menu-item" role="menuitem" onClick={onLogout}>
                  Logout
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </header>
  );
}

// ─── Which top-level entries are collapsible groups ────────────────────────
//
// Duplicated from `lib/navAccess.js` rather than imported: the design system is
// a standalone layer and must not reach up into an app module. The shape is two
// fields, so a copy is cheaper than an inversion of the dependency — but if a
// third field is ever added, move the predicate down here and have navAccess
// import it, not the other way round.
const isNavSection = (entry) => Boolean(entry && entry.section && Array.isArray(entry.items));

/** Read the persisted open-group set, tolerating a browser that refuses storage. */
function readOpenSections(key) {
  if (!key) return null;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    // Private windows, cleared site data and browsers set to block storage all
    // land here. A menu that throws is worse than a menu that forgets.
    return null;
  }
}

export function Sidebar({
  brandIcon,
  brandText,
  items,
  onLogout,
  collapsed: collapsedProp,
  onToggleCollapse,
  hideBrand,
  showThemeToggle = true,
  showFooter = true,
  drawer = false,
  drawerOpen = false,
  // { <statusKey>: { tone: 'ok' | 'warn' | 'bad' | 'idle', title: '…' } } — an
  // entry declaring `status: 'tally'` draws the dot found under that key. The
  // shell knows how to render a dot and nothing about what it means.
  statuses,
  // { tasks, compliance, todo, approvals } — an item naming one of these in
  // `countKey` wears it as a pill. A key that is absent (the caller may not
  // see that section) or zero renders nothing: an empty pill is noise, and a
  // grey "0" beside Approvals is worse than no pill at all.
  counts,
  // The two daily shortcuts above the first divider.
  pinned,
  // { title, subtitle, initials } for the firm row at the bottom.
  footer,
  onFooterClick,
  // Where to remember which groups this user left open. Per user, because one
  // browser routinely holds several accounts here (a CA is a partner at one
  // firm and an article at another) and their menus are not the same menu.
  groupStateKey,
}) {
  const location = useLocation();

  // ─── Hover-to-peek on the icon rail ──────────────────────────────────────
  //
  // Collapsed, the sidebar is twenty icons and no words. Peeking widens it
  // back OVER the page while the pointer is on it, then lets it go — the
  // rail stays useful without costing the content its width.
  //
  // ⚠️ IT OVERLAYS, IT DOES NOT PUSH. The main column's margin is driven by
  // AppLayout's own `collapsed` state, which peeking deliberately does not
  // touch — so nothing reflows. Reflowing the page under the cursor every
  // time somebody reaches for the scrollbar would be unusable.
  //
  // ⚠️ POINTER-ONLY. `(hover: hover) and (pointer: fine)` keeps this off
  // touch screens, where a "hover" is a tap and the rail would expand when
  // somebody meant to press an icon.
  const canHover = useMediaQuery('(hover: hover) and (pointer: fine)');
  const [peek, setPeek] = useState(false);
  const peekTimer = useRef(null);

  // ⚠️ ASYMMETRIC DELAYS, AND BOTH MATTER. Opening waits long enough that a
  // cursor crossing the rail on its way somewhere else does not trigger it;
  // closing waits longer still, because the pointer routinely leaves the
  // sidebar for a moment while travelling down a long menu, and a panel that
  // snaps shut on that is one you have to chase.
  const schedulePeek = (next) => {
    if (peekTimer.current) clearTimeout(peekTimer.current);
    peekTimer.current = setTimeout(() => setPeek(next), next ? 90 : 220);
  };

  // A pending timer that fires after unmount would set state on a dead
  // component; one that fires after the rail is expanded would leave `peek`
  // true underneath it and make the next collapse open instead of close.
  useEffect(() => {
    if (!collapsedProp && peek) setPeek(false);
    return () => {
      if (peekTimer.current) clearTimeout(peekTimer.current);
    };
  }, [collapsedProp, peek]);

  const peekable = collapsedProp && canHover && !drawer;
  // Everything below reads `collapsed` exactly as it did before — the rail
  // and a peeking rail differ only in whether the labels are there.
  const collapsed = collapsedProp && !(peekable && peek);

  // Items can be a plain { to, icon, label }, a parent with a `children` array
  // rendered as an inline expandable sub-group (not a floating flyout — that
  // overlapped the items below it), or a { section, items } collapsible group.
  //
  // ⚠️ THE WALK GOES ALL THE WAY DOWN. Three levels exist once groups are in
  // play (section → item → children), and an active-route match that stopped at
  // two would leave "Deleted Tasks" never highlighting the menu it lives in.
  //
  // ⚠️ THE PINNED BLOCK IS PART OF THE WALK. Its entries are no longer repeated
  // inside a group, so a list built from `items` alone would never match the
  // route the user is actually on — Tasks would sit unhighlighted on the one
  // row they opened it from.
  const topLevel = [...(pinned || []), ...items];
  // Everything above the first collapsible group (Dashboard / AI / Tally) is
  // drawn before the pinned block; everything from that group on is drawn
  // after it. A list with no groups splits into itself and nothing.
  const firstGroupAt = items.findIndex(isNavSection);
  const leadItems = firstGroupAt === -1 ? items : items.slice(0, firstGroupAt);
  const restItems = firstGroupAt === -1 ? [] : items.slice(firstGroupAt);
  const flatItems = topLevel.flatMap((entry) => {
    const own = isNavSection(entry) ? entry.items : [entry];
    return own.flatMap((item) => item.children || [item]);
  });

  // Longest-prefix match so a nav item stays highlighted on its own
  // sub-pages (e.g. "Tasks" while on /org-admin/tasks/create) without a
  // shorter sibling route (e.g. "Dashboard" at /org-admin) swallowing it.
  const activeItem = flatItems.reduce((best, item) => {
    if (!item.to) return best;
    const matches = location.pathname === item.to || location.pathname.startsWith(`${item.to}/`);
    if (!matches) return best;
    return !best || item.to.length > best.to.length ? item : best;
  }, null);
  const isActive = (path) => activeItem?.to === path;
  // `child.to &&` guards groups whose children are all routeless (e.g. the
  // "Coming soon" bundle) — otherwise undefined === activeItem?.to (also
  // undefined when nothing matches) would falsely mark the group active.
  const isParentActive = (item) => item.children?.some((child) => child.to && child.to === activeItem?.to);

  // Whichever sub-menu (Tasks / Approvals) contains the current route starts
  // expanded. One at a time: these are two or three views of one page, and two
  // open at once is just a longer list.
  const [openMenu, setOpenMenu] = useState(() => {
    const activeParent = flatItems.length
      ? topLevel
          .flatMap((entry) => (isNavSection(entry) ? entry.items : [entry]))
          .find((item) => isParentActive(item))
      : null;
    return activeParent?.label || null;
  });

  // ─── Which GROUPS are open ────────────────────────────────────────────────
  //
  // The group holding the current page, plus anything the user has opened since
  // and not closed. Several may be open at once, unlike the sub-menus above:
  // a group is a filing cabinet drawer, not a tab strip, and forcing it shut
  // because you opened another one is how people stop using them.
  const activeSectionLabel =
    items.find(
      (entry) =>
        isNavSection(entry) &&
        entry.items.some((item) => (item.children || [item]).some((sub) => sub.to && sub.to === activeItem?.to))
    )?.section || null;

  const [openSections, setOpenSections] = useState(() => {
    const stored = readOpenSections(groupStateKey);
    // ⚠️ A STORED SET IS HONOURED EVEN WHEN EMPTY — somebody who closed every
    // group meant it, and re-opening one on their behalf reads as the setting
    // not sticking. Only the absence of a stored value falls back to the
    // first-run default below.
    if (stored) return stored;
    if (activeSectionLabel) return [activeSectionLabel];
    // ⚠️ THE FIRST GROUP OPENS WHEN NO GROUP HOLDS THE PAGE, and that case is
    // not rare — it is the DASHBOARD, which is pinned, which is where every
    // session starts. "Collapse everything except the current page's group"
    // answered "collapse everything" there, so a brand-new user's first sight
    // of the menu was seven closed headings and no links at all: a sidebar
    // that looks broken on the one screen everybody lands on first.
    const first = items.find(isNavSection);
    return first ? [first.section] : [];
  });

  // ⚠️ NOT ON MOUNT, AND THAT IS THE WHOLE FIX. Landing in a shut group should
  // open it — but this component REMOUNTS on every navigation (each cockpit
  // page renders its own layout), so an effect that fired on mount re-opened
  // the active group on every single click. Somebody working inside Work could
  // close it and watch it spring back on the next page, for ever: the setting
  // looked broken because it was being overruled a fraction of a second after
  // it was read back from storage.
  //
  // A stored set is now honoured exactly as written, and this only acts when
  // the active section CHANGES within one mount. Which group holds the page is
  // still said out loud — `ds-sidebar-section-active` colours its heading, and
  // a shut group carries its items' badges.
  const mountedSection = useRef(activeSectionLabel);
  useEffect(() => {
    if (!activeSectionLabel || mountedSection.current === activeSectionLabel) return;
    mountedSection.current = activeSectionLabel;
    setOpenSections((prev) => (prev.includes(activeSectionLabel) ? prev : [...prev, activeSectionLabel]));
  }, [activeSectionLabel]);

  useEffect(() => {
    if (!groupStateKey) return;
    try {
      window.localStorage.setItem(groupStateKey, JSON.stringify(openSections));
    } catch {
      // Nothing to do and nothing worth saying: the menu still works, it just
      // forgets between visits.
    }
  }, [groupStateKey, openSections]);

  const toggleSection = (label) =>
    setOpenSections((prev) => (prev.includes(label) ? prev.filter((s) => s !== label) : [...prev, label]));

  const classes = [
    'ds-sidebar',
    collapsed ? 'ds-sidebar-collapsed' : '',
    // Only while peeking: the shadow is what says it is floating ABOVE the
    // page rather than having pushed it aside.
    collapsedProp && !collapsed ? 'ds-sidebar-peek' : '',
    drawer ? 'ds-sidebar-drawer' : '',
    drawer && drawerOpen ? 'ds-sidebar-drawer-open' : '',
  ]
    .filter(Boolean)
    .join(' ');

  // The dot beside an entry that declared a `status` key. Absent when the shell
  // was handed nothing for it, so a status still loading shows no dot rather
  // than a grey one claiming to be an answer.
  const statusDot = (item) => {
    const s = item.status && statuses ? statuses[item.status] : null;
    if (!s) return null;
    return (
      <span
        className={`ds-sidebar-status ds-sidebar-status-${s.tone || 'idle'}`}
        title={s.title || ''}
        aria-label={s.title || ''}
        role="img"
      />
    );
  };

  // The workload pill. `alert` is the terracotta variant, used only where the
  // number is a warning rather than a quantity — filings already past their
  // deadline. Everything else is the neutral grey.
  const countPill = (item) => {
    if (!item.countKey || !counts) return null;
    const n = counts[item.countKey];
    // ⚠️ `> 0`, SO ZERO AND "NOT ALLOWED TO KNOW" BOTH RENDER NOTHING. They
    // are different facts, but the honest display of each is the same: no pill.
    if (!(n > 0)) return null;
    return (
      <span className={`ds-sidebar-pill${item.countTone === 'alert' ? ' ds-sidebar-pill-alert' : ''}`}>
        {n > 999 ? '999+' : n}
      </span>
    );
  };

  // ─── One nav entry ────────────────────────────────────────────────────────
  //
  // Pulled out of the map so a group can render its own children through the
  // very same function. Two copies would drift the first time anything gained
  // a badge, a trailing icon or a status dot.
  const renderItem = (item, index) => {
    if (item.children) {
      const parentActive = isParentActive(item);

      // Collapsed (icon-only) sidebar has no room for inline indentation —
      // just link straight to the first routed child instead of trying to
      // expand in place. A group with no routed children at all renders as an
      // inert icon.
      if (collapsed) {
        const firstChildTo = item.children.find((c) => c.to && !c.soon)?.to;
        if (!firstChildTo) {
          return (
            <button
              key={item.label}
              type="button"
              className="ds-sidebar-item ds-sidebar-item-soon"
              title={`${item.label} — coming soon`}
            >
              <span className="ds-sidebar-item-icon" aria-hidden="true">{item.icon}</span>
            </button>
          );
        }
        return (
          <Link
            key={item.label}
            to={firstChildTo}
            className={`ds-sidebar-item ${parentActive ? 'ds-sidebar-item-active' : ''}`}
            title={item.label}
          >
            {parentActive && (
              <motion.span
                className="ds-sidebar-active-bar"
                layoutId="ds-sidebar-active-bar"
                transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
              />
            )}
            <span className="ds-sidebar-item-icon" aria-hidden="true">{item.icon}</span>
          </Link>
        );
      }

      const isOpen = openMenu === item.label;
      return (
        <div key={item.label} className="ds-sidebar-group">
          <button
            type="button"
            className={`ds-sidebar-item ${parentActive ? 'ds-sidebar-item-active' : ''}`}
            onClick={() => setOpenMenu(isOpen ? null : item.label)}
            aria-expanded={isOpen}
            title={item.label}
          >
            {parentActive && (
              <motion.span
                className="ds-sidebar-active-bar"
                layoutId="ds-sidebar-active-bar"
                transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
              />
            )}
            <span className="ds-sidebar-item-icon" aria-hidden="true">{item.icon}</span>
            <span className="ds-sidebar-item-label">{item.label}</span>
            {countPill(item)}
            {!countPill(item) && item.badge && <span className="ds-sidebar-badge">{item.badge}</span>}
            <motion.span
              className="ds-sidebar-dropdown-caret"
              animate={{ rotate: isOpen ? 180 : 0 }}
              transition={{ duration: 0.15 }}
              aria-hidden="true"
            >
              ▾
            </motion.span>
          </button>

          {/* Same CSS open/close as a section body — see the note there for why
              this is not an AnimatePresence height animation. */}
          <div className={`ds-sidebar-reveal${isOpen ? ' ds-sidebar-reveal-open' : ''}`}>
            <div className="ds-sidebar-subnav">
              {item.children.map((child) =>
                child.soon || !child.to ? (
                  // Not-yet-built feature: inert sub-item, no navigation.
                  <button
                    key={child.label}
                    type="button"
                    className="ds-sidebar-subitem ds-sidebar-item-soon"
                    title={`${child.label} — coming soon`}
                  >
                    {child.icon && <span className="ds-sidebar-item-icon" aria-hidden="true">{child.icon}</span>}
                    <span>{child.label}</span>
                  </button>
                ) : (
                  <Link
                    key={child.to}
                    to={child.to}
                    className={`ds-sidebar-subitem ${isActive(child.to) ? 'ds-sidebar-subitem-active' : ''}`}
                  >
                    {child.icon && <span className="ds-sidebar-item-icon" aria-hidden="true">{child.icon}</span>}
                    <span>{child.label}</span>
                  </Link>
                )
              )}
            </div>
          </div>
        </div>
      );
    }

    // A bare separator line between nav groups.
    if (item.divider) {
      return <div key={`divider-${index}`} className="ds-sidebar-divider" aria-hidden="true" />;
    }

    const active = isActive(item.to);
    const dot = statusDot(item);
    const pill = countPill(item);
    const inner = (
      <>
        {active && (
          <motion.span
            className="ds-sidebar-active-bar"
            layoutId="ds-sidebar-active-bar"
            transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
          />
        )}
        <span className="ds-sidebar-item-icon" aria-hidden="true">{item.icon}</span>
        {!collapsed && <span className="ds-sidebar-item-label">{item.label}</span>}
        {!collapsed && (item.badge || item.trailing || dot || pill) && (
          <span className="ds-sidebar-item-trailing">
            {dot}
            {pill}
            {/* ⚠️ ONE NUMBER PER ROW. An item can declare both a `countKey`
                (how much work is open) and a `notify` section (how many
                notifications are unread) — Tasks declares both. Rendering the
                pair puts "29" beside "3" on one row with nothing saying which
                is which. The workload figure is the one the design asks for and
                the one people act on; the unread count is still on the bell. */}
            {!pill && item.badge && <span className="ds-sidebar-badge">{item.badge}</span>}
            {item.trailing && <span className="ds-sidebar-trailing-icon" aria-hidden="true">{item.trailing}</span>}
          </span>
        )}
        {/* On the icon rail the label is gone, so the dot rides the icon itself
            — a connector that has stopped syncing must still be visible to
            somebody working with the sidebar collapsed. */}
        {collapsed && dot && <span className="ds-sidebar-status-rail">{dot}</span>}
      </>
    );

    // External link (e.g. "Your Website") opens in a new tab.
    if (item.external) {
      return (
        <a
          key={item.label}
          href={item.href || '#'}
          target="_blank"
          rel="noreferrer"
          className="ds-sidebar-item"
          title={item.label}
        >
          {inner}
        </a>
      );
    }

    // Placeholder for a feature that isn't wired up yet — rendered for visual
    // parity, but inert (no navigation).
    if (item.soon || !item.to) {
      return (
        <button
          key={item.label}
          type="button"
          className="ds-sidebar-item ds-sidebar-item-soon"
          title={`${item.label} — coming soon`}
        >
          {inner}
        </button>
      );
    }

    return (
      <Link
        key={item.to}
        to={item.to}
        className={`ds-sidebar-item ${active ? 'ds-sidebar-item-active' : ''}`}
        title={item.label}
      >
        {inner}
      </Link>
    );
  };

  return (
    // As a closed drawer it's off-canvas, so hide it from AT and from tab
    // order too — CSS visibility alone wouldn't be enough if it animates.
    <div
      className={classes}
      aria-hidden={drawer && !drawerOpen ? 'true' : undefined}
      onMouseEnter={peekable ? () => schedulePeek(true) : undefined}
      onMouseLeave={peekable ? () => schedulePeek(false) : undefined}
      // ⚠️ FOCUS OPENS IT TOO, and that is not a nicety: tabbing into a rail of
      // unlabelled icons is navigating blind. The CAPTURE phase, because focus
      // lands on a descendant rather than on this div.
      onFocusCapture={peekable ? () => schedulePeek(true) : undefined}
      onBlurCapture={
        peekable
          ? (e) => {
              // Moving between two items inside the sidebar fires a blur; only
              // focus leaving the sidebar entirely should close it.
              if (!e.currentTarget.contains(e.relatedTarget)) schedulePeek(false);
            }
          : undefined
      }
    >
      {!hideBrand && (
        <div className="ds-sidebar-header">
          <div className="ds-sidebar-brand">
            {brandIcon && <span className="ds-sidebar-brand-icon">{brandIcon}</span>}
            {!collapsed && <span className="ds-sidebar-brand-text">{brandText}</span>}
          </div>
          {/* ⚠️ READS `collapsedProp`, NOT `collapsed`. While peeking the second
              is false, so this button would offer to "collapse" a sidebar that
              is already collapsed and merely being looked at. */}
          <button
            className="ds-sidebar-collapse-btn"
            onClick={onToggleCollapse}
            title={collapsedProp ? 'Expand' : 'Collapse'}
            aria-label={collapsedProp ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsedProp ? '→' : '←'}
          </button>
        </div>
      )}

      <nav className="ds-sidebar-nav">
        {/* ⚠️ THE ALWAYS-ON ROWS COME FIRST, THEN THE PINS. The design is
            Dashboard / AI / Tally, a hairline, PINNED, then the groups — and
            the pinned block was rendering above the lot of them, so the three
            rows that are supposed to be in the same place on every page sat
            below a heading that did not describe them. `leadItems` is the run
            of ungrouped entries before the first group; the employee shell has
            no pins at all, so its flat list is untouched. */}
        {leadItems.map((entry, index) => renderItem(entry, index))}
        {/* ⚠️ THE HAIRLINE AND THE "PINNED" LABEL BELONG TO THIS BLOCK, not to
            the nav as a whole — they mark where the always-on rows end. On the
            rail there is no room for a label, so the pins render as plain
            icons and the hairline alone keeps them apart. */}
        {pinned && pinned.length > 0 && (
          <>
            <div className="ds-sidebar-rule" aria-hidden="true" />
            {!collapsed && <div className="ds-sidebar-eyebrow">Pinned</div>}
            {pinned.map((item, i) => renderItem(item, i))}
          </>
        )}
        {restItems.map((entry, i) => {
          // Offset so a divider's key stays unique against `leadItems`.
          const index = firstGroupAt + i;
          if (!isNavSection(entry)) return renderItem(entry, index);

          // ⚠️ ON THE ICON RAIL A GROUP HAS NO HEADING AND NO CHEVRON — there is
          // no room for either, and a heading that renders as a stray tooltip is
          // worse than none. The items simply flow as icons, which is exactly
          // what the rail looked like before groups existed. A thin rule keeps
          // the boundary visible so the icons are still in recognisable runs.
          if (collapsed) {
            return (
              <div key={entry.section} className="ds-sidebar-rail-group">
                {entry.items.map((item, i) => renderItem(item, i))}
              </div>
            );
          }

          const open = openSections.includes(entry.section);
          const holdsActive = activeSectionLabel === entry.section;
          return (
            <div
              key={entry.section}
              className={`ds-sidebar-section ${holdsActive ? 'ds-sidebar-section-active' : ''}`}
            >
              <button
                type="button"
                className="ds-sidebar-section-header"
                onClick={() => toggleSection(entry.section)}
                aria-expanded={open}
              >
                <span className="ds-sidebar-section-label">{entry.section}</span>
                {/* ⚠️ THE SUM ONLY SHOWS WHILE THE GROUP IS SHUT. Open, the same
                    counts are on the rows one line below, and two copies of one
                    number read as two different numbers. */}
                {!open && entry.sectionBadge && (
                  <span className="ds-sidebar-badge ds-sidebar-section-badge">{entry.sectionBadge}</span>
                )}
                {/* ⚠️ TWO GLYPHS, NOT ONE ROTATED. A rotated "›" lands on a
                    diagonal that reads as neither state at small sizes; the
                    design names "⌄" open and "›" shut, and both sit upright. */}
                <span className="ds-sidebar-section-caret" aria-hidden="true">
                  {open ? '⌄' : '›'}
                </span>
              </button>

              {/* ⚠️ A CSS TRANSITION, NOT `AnimatePresence`. The height
                  animation this replaces could not be relied on to finish: under
                  React 18 StrictMode an exiting AnimatePresence child is liable
                  to be left mounted for good, so the group's items stayed on
                  screen at full height while `aria-expanded` said shut — after
                  which neither click did anything visible and the whole menu
                  read as frozen. The group is always in the DOM now and grows
                  from `0fr` to `1fr`, which has no completion callback to miss.
                  `visibility` is what keeps a shut group out of the tab order,
                  delayed on close so it does not blink away mid-transition. */}
              <div className={`ds-sidebar-reveal${open ? ' ds-sidebar-reveal-open' : ''}`}>
                <div className="ds-sidebar-section-body">
                  {entry.items.map((item, i) => renderItem(item, i))}
                </div>
              </div>
            </div>
          );
        })}
      </nav>

      {/* ⚠️ STICKY, AND OUTSIDE THE SCROLLING LIST. Which firm and which role
          you are acting as is the one thing that must never scroll away — a
          menu that can silently belong to another practice is the failure the
          whole multi-firm identity model exists to prevent. */}
      {footer && (
        <div className="ds-sidebar-firm">
          <div className="ds-sidebar-rule" aria-hidden="true" />
          <button
            type="button"
            className="ds-sidebar-firm-row"
            onClick={onFooterClick}
            title={collapsed ? `${footer.title} — ${footer.subtitle}` : undefined}
          >
            <span className="ds-sidebar-firm-avatar" aria-hidden="true">{footer.initials}</span>
            {!collapsed && (
              <>
                <span className="ds-sidebar-firm-text">
                  <span className="ds-sidebar-firm-name">{footer.title}</span>
                  <span className="ds-sidebar-firm-role">{footer.subtitle}</span>
                </span>
                <span className="ds-sidebar-firm-caret" aria-hidden="true">⌄</span>
              </>
            )}
          </button>
        </div>
      )}

      {showFooter && (
        <div className="ds-sidebar-footer">
          <button className="ds-sidebar-item ds-sidebar-logout" onClick={onLogout} title="Logout">
            <span className="ds-sidebar-item-icon" aria-hidden="true">⏻</span>
            {!collapsed && <span className="ds-sidebar-item-label">Logout</span>}
          </button>
          {showThemeToggle && <ThemeToggle collapsed={collapsed} />}
        </div>
      )}
    </div>
  );
}

export function AppHeader({ title, subtitle, right }) {
  return (
    <header className="ds-app-header">
      <div className="ds-app-header-left">
        <h1>{title}</h1>
        {subtitle && <p>{subtitle}</p>}
      </div>
      {right && <div className="ds-app-header-right">{right}</div>}
    </header>
  );
}

function AppLayout({
  brandIcon,
  brandText,
  items,
  onLogout,
  title,
  subtitle,
  headerRight,
  children,
  variant,
  user,
  companyName,
  notificationCount,
  renderNotifications,
  onSearch,
  searchPlaceholder,
  // Forwarded straight through to the Sidebar — the layout has no opinion
  // about either, it just refuses to make the caller reach past it.
  navStatuses,
  navGroupStateKey,
  navCounts,
  navPinned,
  navFooter,
  onNavFooterClick,
}) {
  const [collapsed, setCollapsed] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const isMobile = useMediaQuery(MOBILE_QUERY);
  const location = useLocation();
  // Both the org-admin and super-admin shells use the global TopBar chrome
  // (brand + search + avatar menu, no sidebar footer). The default (no variant)
  // keeps the plain sidebar-with-footer layout.
  const hasTopBar = variant === 'orgadmin' || variant === 'admin';
  // The drawer needs a trigger, and the only one is the TopBar hamburger —
  // the plain shell keeps its inline sidebar at every width.
  const isDrawer = isMobile && hasTopBar;

  // The hamburger means "collapse to an icon rail" on desktop but "open the
  // nav" on mobile, where there is no rail to collapse to.
  const toggleCollapsed = () => {
    if (isDrawer) setDrawerOpen((o) => !o);
    else setCollapsed((c) => !c);
  };

  // Picking a destination should get the nav out of the way, and growing back
  // to desktop must not leave a stale open drawer behind.
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!isDrawer) setDrawerOpen(false);
  }, [isDrawer]);

  // While the drawer covers the page, Escape closes it and the page behind
  // the scrim must not scroll.
  useEffect(() => {
    if (!isDrawer || !drawerOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setDrawerOpen(false);
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isDrawer, drawerOpen]);

  return (
    <div className={`ds-root ds-app-shell ${hasTopBar ? 'ds-app-shell--orgadmin' : ''}`}>
      {hasTopBar && (
        <TopBar
          brandIcon={brandIcon}
          brandText={brandText}
          collapsed={isDrawer ? !drawerOpen : collapsed}
          onToggleCollapse={toggleCollapsed}
          user={user}
          companyName={companyName}
          onLogout={onLogout}
          notificationCount={notificationCount}
          renderNotifications={renderNotifications}
          onSearch={onSearch}
          searchPlaceholder={searchPlaceholder}
        />
      )}
      <div className="ds-app-shell-body">
        <Sidebar
          brandIcon={brandIcon}
          brandText={brandText}
          items={items}
          onLogout={onLogout}
          // A drawer has the room for full labels — the icon-rail collapse
          // only makes sense for the inline desktop sidebar.
          collapsed={isDrawer ? false : collapsed}
          onToggleCollapse={toggleCollapsed}
          hideBrand={hasTopBar}
          showThemeToggle={!hasTopBar}
          showFooter={!hasTopBar}
          drawer={isDrawer}
          drawerOpen={drawerOpen}
          statuses={navStatuses}
          groupStateKey={navGroupStateKey}
          counts={navCounts}
          pinned={navPinned}
          footer={navFooter}
          onFooterClick={onNavFooterClick}
        />
        <AnimatePresence>
          {isDrawer && drawerOpen && (
            <motion.button
              type="button"
              className="ds-sidebar-scrim"
              aria-label="Close navigation"
              onClick={() => setDrawerOpen(false)}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
            />
          )}
        </AnimatePresence>
        <div className={`ds-app-main-wrap ${collapsed ? 'ds-app-main-wrap-collapsed' : ''}`}>
          <AppHeader title={title} subtitle={subtitle} right={headerRight} />
          <main className="ds-app-main">
            <PageTransition>{children}</PageTransition>
          </main>
        </div>
      </div>
    </div>
  );
}

export default AppLayout;
